import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { CATEGORIES, periodRange, periodLabel } from "../lib/notes";
import { useNotes } from "../lib/notesContext";

const inp = "h-9 bg-[#0B0F19] border-slate-700 text-slate-100 font-mono text-xs [color-scheme:dark]";
const PERIODS = [["day", "Day"], ["week", "Week"], ["month", "Month"], ["quarter", "Quarter"], ["range", "Date range"]];

const initValue = (n, d) => {
  if (!n) return d.value || new Date().toISOString().slice(0, 10);
  if (n.period === "month") return n.start.slice(0, 7);
  if (n.period === "quarter") return `${n.start.slice(0, 4)}-Q${Math.floor((+n.start.slice(5, 7) - 1) / 3) + 1}`;
  return n.start;
};

const Field = ({ label, children }) => <div className="space-y-1.5"><p className="label-mono">{label}</p>{children}</div>;

const PeriodInput = ({ period, value, setValue, endValue, setEndValue }) => {
  if (period === "month") return <Input type="month" value={value.slice(0, 7)} onChange={(e) => setValue(e.target.value)} className={inp} data-testid="note-month-input" />;
  if (period === "quarter") {
    const [y, q] = value.includes("-Q") ? value.split("-Q") : [value.slice(0, 4), String(Math.floor(+value.slice(5, 7) / 3) + 1 || 1)];
    return (
      <div className="flex gap-2">
        <Input type="number" value={y} onChange={(e) => setValue(`${e.target.value}-Q${q}`)} className={`${inp} w-24`} data-testid="note-quarter-year" />
        <Select value={q} onValueChange={(v) => setValue(`${y}-Q${v}`)}>
          <SelectTrigger className={`${inp} w-24`} data-testid="note-quarter-select"><SelectValue /></SelectTrigger>
          <SelectContent>{["1", "2", "3", "4"].map((x) => <SelectItem key={x} value={x}>Q{x}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div className="flex gap-2 items-center">
      <Input type="date" value={value.length === 10 ? value : ""} onChange={(e) => setValue(e.target.value)} className={inp} data-testid="note-date-input" />
      {period === "range" && <><span className="text-slate-500">→</span><Input type="date" value={endValue} onChange={(e) => setEndValue(e.target.value)} className={inp} data-testid="note-end-input" /></>}
    </div>
  );
};

export const NoteForm = ({ open, onOpenChange, note, defaults = {}, pickers = [] }) => {
  const { save } = useNotes();
  const [scope, setScope] = useState(note?.scope || defaults.scope || "team");
  const [picker, setPicker] = useState(note?.picker || defaults.picker || "");
  const [period, setPeriod] = useState(note?.period || defaults.period || "month");
  const [value, setValue] = useState(initValue(note, defaults));
  const [endValue, setEndValue] = useState(note?.end || defaults.end || "");
  const [category, setCategory] = useState(note?.category || defaults.category || "general");
  const [text, setText] = useState(note?.text || "");
  const normValue = period === "month" ? `${value.slice(0, 7)}-01` : value;
  const valid = text.trim() && (scope === "team" || picker) && (period === "quarter" ? value.includes("-Q") : value.length >= 7) && (period !== "range" || endValue);
  const range = valid ? periodRange(period, period === "month" ? value.slice(0, 7) : normValue, endValue) : null;
  const submit = async () => {
    if (!valid) return;
    const ok = await save({ scope, picker: scope === "picker" ? picker : null, period, ...range, category, text: text.trim() }, note?.id);
    if (ok) onOpenChange(false);
  };
  const setP = (p) => {
    setPeriod(p);
    if (p === "quarter" && !value.includes("-Q")) setValue(`${value.slice(0, 4)}-Q${Math.floor((+value.slice(5, 7) - 1) / 3) + 1 || 1}`);
    if (p !== "quarter" && value.includes("-Q")) { const [y, q] = value.split("-Q"); setValue(`${y}-${String((q - 1) * 3 + 1).padStart(2, "0")}-01`); }
    if (p !== "month" && value.length === 7) setValue(`${value}-01`);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#111827] border-slate-800 text-slate-200" data-testid="note-form">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-slate-100">{note ? "Edit note" : "Add commentary"}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Notes are pinned to real calendar dates, so they stay against the right period when the data is refreshed.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="About">
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className={inp} data-testid="note-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="team">Whole team</SelectItem><SelectItem value="picker">A picker</SelectItem></SelectContent>
            </Select>
          </Field>
          {scope === "picker" ? (
            <Field label="Picker">
              <Select value={picker} onValueChange={setPicker}>
                <SelectTrigger className={inp} data-testid="note-picker-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent className="max-h-72">{[...new Set([...(picker ? [picker] : []), ...pickers])].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          ) : <div />}
          <Field label="Period">
            <Select value={period} onValueChange={setP}>
              <SelectTrigger className={inp} data-testid="note-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Topic">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className={inp} data-testid="note-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="col-span-2"><Field label={period === "week" ? "Any day in the week" : "When"}><PeriodInput period={period} value={value} setValue={setValue} endValue={endValue} setEndValue={setEndValue} /></Field></div>
          <div className="col-span-2">
            <Field label="Commentary">
              <Textarea data-testid="note-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={4000} placeholder="e.g. Visits down because of stock-take on 14th; two pickers on annual leave; new WMS release slowed picking…" className="bg-[#0B0F19] border-slate-700 text-slate-100 text-sm" />
            </Field>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2">
          <p className="text-[11px] text-slate-500 mr-auto" data-testid="note-range-preview">{range ? `Pinned to: ${periodLabel({ period, ...range })}` : "Fill in the fields"}</p>
          <button onClick={() => onOpenChange(false)} className="text-xs px-3 py-2 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</button>
          <button data-testid="note-save-button" disabled={!valid} onClick={submit} className="text-xs px-4 py-2 rounded-md bg-ep hover:bg-ep-hover text-white disabled:opacity-50">{note ? "Save" : "Add note"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
